import { createInertiaApp } from "@inertiajs/react"
import type { ComponentType, ReactNode } from "react"

import { AppLayout } from "@/layouts/app-layout"
import "../styles/application.css"
import { applyPalette, loadSelection } from "@/lib/theme"

// Apply the persisted palette/mode before React mounts to avoid a flash.
applyPalette(loadSelection())

type PageComponent = ComponentType<Record<string, unknown>> & {
  layout?: (page: ReactNode) => ReactNode
}

void createInertiaApp({
  // Resolve pages ourselves (instead of the `pages:` shorthand) so every page
  // is wrapped in AppLayout. Inertia treats a function `layout` as persistent,
  // so the shared header survives client-side navigations without remounting.
  resolve: async (name) => {
    const pages = import.meta.glob<{ default: PageComponent }>("../pages/**/*.tsx")
    const importPage = pages[`../pages/${name}.tsx`]
    if (!importPage) throw new Error(`Inertia page not found: ${name}`)
    const { default: component } = await importPage()
    component.layout ??= (node) => <AppLayout>{node}</AppLayout>
    return component
  },

  strictMode: true,

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: "brackets" }
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
